import { random } from 'lodash'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BotContainerErrorIcon from '@/assets/bot-container-error-icon.webp'
import BotContainerIcon from '@/assets/bot-container-icon.webp'
import OpacityAnimation from '@/components/animations/OpacityAnimation'
import BotMessage from '@/components/chat/BotMessage.tsx'
import { Button, Column, Show, Text } from '@/components/core'
import AvatarCard from '@/components/layout/AvatarCard'
import { useMediaDevices } from '@/contexts/MediaDevicesContext.tsx'
import { ROUTES } from '@/types/routes.ts'

const ListeningTest: React.FC = () => {
  const navigate = useNavigate()

  const { hasPermission } = useMediaDevices()

  const [showListenError, setShowListenError] = useState(false)
  const [audioPlaying, setAudioPlaying] = useState(false)

  const audioUrl = useMemo(() => {
    return `/listening-test-audio-${random(1, 2)}.mp3`
  }, [])

  if (showListenError) {
    return (
      <AvatarCard variant="icon" botAvatar={BotContainerErrorIcon}>
        <Column className="h-full flex-1">
          <Column className="flex-1 items-center">
            <Column className="w-full flex-1 items-center space-y-5">
              <Text
                color="600"
                align="center"
                className="max-sm:text-xl sm:text-2xl"
              >
                <Text.Translated
                  color="600"
                  fontWeight="bold"
                  className="max-sm:text-xl sm:text-2xl"
                  translationKey="hmm_listening_test_error"
                />{' '}
                <Text.Translated translationKey="not_hearing_listening_test_error" />
              </Text>
              <Column className="items-center">
                <Text.Translated
                  color="600"
                  align="center"
                  className="max-w-[412px] max-sm:max-w-[280px] max-sm:text-base"
                  translationKey="troubleshoot_message_listening_test_error"
                />
              </Column>
            </Column>
            <Column className="mx-16 w-full max-w-[360px] justify-center">
              <Button.Translated
                onClick={() => setShowListenError(false)}
                translationKey="try_again_listening_test_error"
              />
            </Column>
          </Column>
        </Column>
      </AvatarCard>
    )
  }

  return (
    <AvatarCard variant="icon" botAvatar={BotContainerIcon}>
      <Column className="h-full flex-1">
        <Column className="flex-1 items-center gap-4">
          <Column className="w-full flex-1 items-center space-y-5">
            <Text.Translated
              color="600"
              fontWeight="bold"
              align="center"
              className="max-sm:text-xl sm:text-2xl"
              translationKey="check_sound_setup_states_listening_test"
            />
            <Column className="items-center pb-6">
              <Text.Translated
                color="600"
                align="center"
                className="max-w-[380px] max-sm:max-w-[280px] max-sm:text-base"
                translationKey="audio_test_message_setup_states_listening_test"
              />
            </Column>
            <OpacityAnimation className="flex w-full justify-center">
              <BotMessage
                audioUrl={audioUrl}
                variant="secondary"
                autoPlay
                isTestingAudio
                showControls
                pending={false}
                onStartPlaying={() => setAudioPlaying(true)}
              />
            </OpacityAnimation>
          </Column>
          <Show when={audioPlaying}>
            <OpacityAnimation className="flex w-full justify-center max-sm:flex-col max-sm:space-y-3 sm:max-w-[520px] sm:flex-row sm:space-x-6">
              <Button.Translated
                translationKey="listening_button_setup_states_listening_test"
                grow
                onClick={() =>
                  navigate(
                    hasPermission
                      ? ROUTES.ONBOARDING.RECORDING_TEST
                      : ROUTES.ONBOARDING.MICROPHONE_PERMISSION
                  )
                }
              />
              <Button.Translated
                translationKey="not_listening_button_setup_states_listening_test"
                variant="outline"
                grow
                onClick={() => {
                  setShowListenError(true)
                  setAudioPlaying(false)
                }}
              />
            </OpacityAnimation>
          </Show>
        </Column>
      </Column>
    </AvatarCard>
  )
}

export default ListeningTest
